import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import type { PendingActivityInfo } from "@/types/activity";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { User, Calendar, Trash2 } from "lucide-react";
import { trackFreelancerView } from "@/hooks/use-track-freelancer-view";
import { toUserError } from "@/lib/api-error";

interface FalarComFrilaModalProps {
  isOpen: boolean;
  onClose: () => void;
  freelancerId: string;
  freelancerNome: string;
  existingActivity?: PendingActivityInfo | null;
}

interface UserOption {
  id: string;
  full_name: string | null;
}

export function FalarComFrilaModal({
  isOpen,
  onClose,
  freelancerId,
  freelancerNome,
  existingActivity,
}: FalarComFrilaModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [observacao, setObservacao] = useState("");

  // Handler para excluir atividade existente
  const handleDeleteActivity = async () => {
    if (!existingActivity) return;

    setIsDeleting(true);
    try {
      // Excluir a atividade
      const { error: deleteError } = await supabase
        .from("activities")
        .delete()
        .eq("id", existingActivity.id);

      if (deleteError) throw deleteError;

      // Desmarcar o freelancer
      const { error: updateError } = await supabase
        .from("freelancers")
        .update({
          falar_com_frila: false,
          falar_com_frila_marked_at: null,
          falar_com_frila_marked_by: null,
        })
        .eq("id", freelancerId);

      if (updateError) throw updateError;

      toast({
        title: "Atividade excluida",
        description: `Atividade "Fazer Amizade" de ${freelancerNome} foi removida.`,
      });

      queryClient.invalidateQueries({ queryKey: ["activity_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["freelancers"] });
      onClose();
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao excluir atividade:", error);
      toast({
        title: "Erro",
        description: "Nao foi possivel excluir a atividade. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Registra visualização do perfil quando o modal abre.
  useEffect(() => {
    if (isOpen) trackFreelancerView(freelancerId, "falar_com_frila");
  }, [isOpen, freelancerId]);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const { data, error } = await supabase
          .from("v_profiles_with_access_profile")
          .select("id, full_name, access_profile_slug")
          .in("access_profile_slug", ["admin", "membro"])
          .order("full_name");

        if (error) throw error;

        setUsers(
          data.map((p) => ({
            id: p.id,
            full_name: p.full_name,
          }))
        );
      } catch (error) {
        console.error("Erro ao buscar usuarios:", error);
      }
    }

    if (isOpen && !existingActivity) {
      fetchUsers();
      setSelectedUserId("");
      setObservacao("");
    }
  }, [isOpen, existingActivity]);

  const handleSubmit = async () => {
    if (!selectedUserId) {
      toast({
        title: "Selecione um responsavel",
        description: "Voce precisa selecionar um usuario para atribuir a atividade.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.rpc("create_freelancer_activity", {
        p_freelancer_id: freelancerId,
        p_tipo: "falar_com_frila",
        p_assigned_to: selectedUserId,
        p_observacao: observacao || null,
      });

      if (error) throw error;

      toast({
        title: "Atividade criada",
        description: `Atividade "Fazer Amizade" criada para ${freelancerNome}.`,
      });

      queryClient.invalidateQueries({ queryKey: ["activity_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      // A RPC marca freelancers.falar_com_frila = true (M126); invalidar a lista
      // de freelancers para o badge "Falar com o Frila" aparecer sem reload.
      queryClient.invalidateQueries({ queryKey: ["freelancers"] });
      onClose();
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao criar atividade:", error);
      toast({
        title: "Erro",
        description: "Nao foi possivel criar a atividade. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se existe uma atividade pendente, mostra os detalhes
  if (existingActivity) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Atividade Existente</DialogTitle>
            <DialogDescription>
              Ja existe uma atividade "Fazer Amizade" pendente para{" "}
              <strong>{freelancerNome}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Atribuido a:</span>
              <Badge variant="secondary">
                {existingActivity.assigned_to_name || "Nao atribuido"}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Criado em:</span>
              <span className="text-sm">
                {format(new Date(existingActivity.created_at), "dd/MM/yyyy 'as' HH:mm", {
                  locale: ptBR,
                })}
              </span>
            </div>

            {existingActivity.created_by_name && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Criado por:</span>
                <span className="text-sm">{existingActivity.created_by_name}</span>
              </div>
            )}

            {existingActivity.observacao && (
              <div className="space-y-1">
                <span className="text-sm text-muted-foreground">Observacao:</span>
                <p className="text-sm bg-muted p-2 rounded">{existingActivity.observacao}</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button
              variant="destructive"
              onClick={handleDeleteActivity}
              disabled={isDeleting}
              className="gap-1"
            >
              <Trash2 className="h-4 w-4" />
              {isDeleting ? "Excluindo..." : "Excluir Atividade"}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={isDeleting}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Fazer Amizade</DialogTitle>
          <DialogDescription>
            Criar atividade "Fazer Amizade" para <strong>{freelancerNome}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="assigned-to">Atribuir para</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger id="assigned-to">
                <SelectValue placeholder="Selecione um responsavel..." />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.full_name || "Sem nome"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacao">Observacao (opcional)</Label>
            <Textarea
              id="observacao"
              placeholder="Adicione uma observacao sobre o contato..."
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Criando..." : "Criar Atividade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
